import { useState, useEffect, useCallback } from "react";
import idx from "idx";
import { buildStageHierarchy, findTaskInStages, findStageInStages } from "../utils/costBreakdownHelpers";
import { toDecimal } from "../../../../../../util/decimal-utils";

/**
 * useCostBreakdownApi - Hook for fetching and managing cost breakdown data
 * 
 * SUMMARY:
 * Handles all data fetching for cost breakdown including project, task, stage data,
 * material costs, subcontractor costs, and labor costs. Supports both stage-level
 * and task-level breakdowns with proper filtering and attribution logic.
 * 
 * RESPONSIBILITIES:
 * - Fetch project summary data
 * - Handle stage-level cost breakdown (material, subcontractor, labor)
 * - Handle task-level cost breakdown (material, subcontractor, labor)
 * - Process PO data and filter by stageId/taskId
 * - Set cost type labels
 * - Manage loading states
 * - Fetch global settings and user lists for calculations
 * 
 * @param {Object} params - Route parameters from useCostBreakdownParams
 * @param {string|null} params.projectId - Project ID
 * @param {string|null} params.costType - Cost type (material, labor, subcontractor, all)
 * @param {string|null} params.taskId - Task ID for task-level breakdown
 * @param {string|null} params.finalStageId - Stage ID for stage-level breakdown
 * @param {boolean} params.isStageBreakdown - Whether this is stage-level breakdown
 * @param {Object} props - Component props with action creators
 * @returns {Object} Cost breakdown data and state
 */
export const useCostBreakdownApi = (params, props) => {
  const { projectId, costType, taskId, finalStageId, isStageBreakdown } = params;

  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [stage, setStage] = useState(null);
  const [costDetails, setCostDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [costTypeLabel, setCostTypeLabel] = useState("");
  const [resourceAllocations, setResourceAllocations] = useState([]);
  const [stageHierarchy, setStageHierarchy] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({});

  const _getGlobalSettings = async () => {
    const { data } = await props.getGlobalSettings('LETTERHEAD');
    setGlobalSettings(data || {});
    return data || {};
  };

  const _getUsersList = async () => {
    const status = "ACTIVE";
    const list = await props.getUsersList({ status });
    const usersList = (Array.isArray(list) && list.map(({ id, firstName, lastName }) => ({
      key: (firstName === null ? "" : firstName) + " " + (lastName === null ? "" : lastName),
      value: id,
    }))) || [];
    return usersList;
  };

  const setCostTypeLabelFromType = (type) => {
    if (type === "material") {
      setCostTypeLabel("Material");
    } else if (type === "labor") {
      setCostTypeLabel("Resource");
    } else if (type === "subcontractor") {
      setCostTypeLabel("Subcontractor");
    } else if (type === "all") {
      setCostTypeLabel("All");
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!projectId) {
        setIsLoading(false);
        return;
      }

      // Get project data: reuse from Redux if already loaded for this project, else fetch
      const isProjectInRedux =
        props.projectSummary?.project?.projectId === projectId &&
        Array.isArray(props.projectSummary?.sectionData) &&
        props.projectSummary?.sectionData?.length > 0;

      const projectData = isProjectInRedux
        ? {
            ...(props.projectSummary.project.res || props.projectSummary.project || {}),
            stages: props.projectSummary.sectionData,
          }
        : await props.getProjectSummary(projectId);
      setProject(projectData);

      // Handle stage-level breakdown
      if (isStageBreakdown && finalStageId) {
        const foundStage = projectData.stages ? findStageInStages(projectData.stages, finalStageId) : null;

        if (foundStage) {
          setStage(foundStage);
          const hierarchy = buildStageHierarchy(projectData.stages, finalStageId);
          setStageHierarchy(hierarchy);

          // For flagged stages (is_attribution_level: true), we only want data DIRECTLY attributed to the stage,
          // not data from tasks within that stage. This is because flagged stages have their own direct attribution.
          const isFlaggedStage = foundStage.is_attribution_level === true;

          setCostTypeLabelFromType(costType);

          // Fetch stage-attributed cost details using existing services
          // Material and Subcontractor: Fetch all POs and filter invoice items by stageId
          if (costType === "material" || costType === "all") {
            const materialCosts = [];
            // Fetch all material POs for the project
            const materialPODetails = await props.getvendorsPOList({ vendorType: 'Supplier', projectId, isCostBreakdown: true });

            // Handle response structure: could be { list: [...] } or { data: { list: [...] } }
            const materialList = materialPODetails?.data?.list || materialPODetails?.list || [];

            if (Array.isArray(materialList) && materialList.length > 0) {
              materialList.forEach(po => {
                // Filter invoice items by stageId
                if (po.items) {
                  const parsedItems = typeof po.items === 'string' ? JSON.parse(po.items) : po.items;
                  const stageAttributedItems = [];

                  if (Array.isArray(parsedItems)) {
                    parsedItems.forEach(entry => {
                      if (!entry) return;
                      // Check if entry is attributed to this stage
                      let isStageAttributed = false;

                      if (isFlaggedStage) {
                        // Flagged stage: only direct stage attribution
                        if (entry.stageId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'STAGE' && entry.taskId === finalStageId) {
                          isStageAttributed = true;
                        }
                      } else {
                        // Non-flagged stage: include data from tasks within the stage
                        if (entry.stageId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'STAGE' && entry.taskId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'TASK' && entry.taskId) {
                          // For TASK type, check if the task belongs to this stage
                          const task = projectData.stages
                            ? findTaskInStages(projectData.stages, entry.taskId)
                            : null;
                          if (task && task.stageId === finalStageId) {
                            isStageAttributed = true;
                          }
                        }
                      }

                      if (isStageAttributed) {
                        // Supplier PO invoice: entry.items is an array of item lines
                        const nestedItems = idx(entry, _ => _.items);
                        if (Array.isArray(nestedItems)) {
                          nestedItems.forEach(item => {
                            const amountPaid = parseFloat(item?.amountPaid || 0) || 0;
                            if (amountPaid !== 0) {
                              stageAttributedItems.push({
                                amountPaid,
                                item
                              });
                            }
                          });
                        }
                      }
                    });
                  }

                  // If this invoice has stage-attributed items, add it to costs
                  if (stageAttributedItems.length > 0) {
                    const totalAmount = stageAttributedItems.reduce((sum, item) => sum + item.amountPaid, 0);
                    materialCosts.push({
                      id: po.invoiceId || `po-${Math.random()}`,
                      type: 'PO',
                      poId: po.poId,
                      poNumber: po.poNumber || 'N/A',
                      procurementCode: po.fId,
                      materialName: po.vendorName || 'Unknown Material',
                      partNo: po.poId || 'N/A',
                      invoiceNo: po.invoiceNo || 'N/A',
                      amount: totalAmount,
                      subTotal: totalAmount,
                      date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                      invoiceData: po,
                      invoiceId: po.invoiceId
                    });
                  }
                }
              });
            }

            if (costType === "material") {
              setCostDetails(materialCosts);
            } else {
              setCostDetails(materialCosts);
            }
          }

          // Subcontractor costs
          if (costType === "subcontractor" || costType === "all") {
            const subcontractorCosts = [];
            // Fetch all subcontractor POs for the project
            const subcontractorPODetails = await props.getvendorsPOList({ vendorType: 'sub-contractor', projectId, isCostBreakdown: true });

            // Handle response structure: could be { list: [...] } or { data: { list: [...] } }
            const subcontractorList = subcontractorPODetails?.data?.list || subcontractorPODetails?.list || [];

            if (Array.isArray(subcontractorList) && subcontractorList.length > 0) {
              subcontractorList.forEach(po => {
                // Filter invoice items by stageId
                if (po.items) {
                  const parsedItems = typeof po.items === 'string' ? JSON.parse(po.items) : po.items;
                  const stageAttributedItems = [];

                  if (Array.isArray(parsedItems)) {
                    parsedItems.forEach(entry => {
                      if (!entry) return;
                      // Check if entry is attributed to this stage
                      let isStageAttributed = false;

                      if (isFlaggedStage) {
                        // Flagged stage: only direct stage attribution
                        if (entry.stageId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'STAGE' && entry.taskId === finalStageId) {
                          isStageAttributed = true;
                        }
                      } else {
                        // Non-flagged stage: include data from tasks within the stage
                        if (entry.stageId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'STAGE' && entry.taskId === finalStageId) {
                          isStageAttributed = true;
                        } else if (entry.taskType === 'TASK' && entry.taskId) {
                          // For TASK type, check if the task belongs to this stage
                          const task = projectData.stages
                            ? findTaskInStages(projectData.stages, entry.taskId)
                            : null;
                          if (task && task.stageId === finalStageId) {
                            isStageAttributed = true;
                          }
                        }
                      }

                      if (isStageAttributed) {
                        // Subcontractor WO invoice: entry.claimAmount or entry.invoiceClaim is the GST-exclusive amount, falling back to amountPaid
                        const claim = entry?.claimAmount !== undefined && entry?.claimAmount !== null
                          ? entry.claimAmount
                          : (entry?.invoiceClaim !== undefined && entry?.invoiceClaim !== null ? entry.invoiceClaim : (entry?.amountPaid || 0));
                        const amountPaid = parseFloat(claim) || 0;
                        if (amountPaid !== 0) {
                          stageAttributedItems.push({ amountPaid });
                        }
                      }
                    });
                  }

                  // If this invoice has stage-attributed items, add it to costs
                  if (stageAttributedItems.length > 0) {
                    const totalAmount = stageAttributedItems.reduce((sum, item) => sum + item.amountPaid, 0);
                    subcontractorCosts.push({
                      id: po.invoiceId || `sc-${Math.random()}`,
                      type: 'Subcontractor Invoice',
                      poId: po.poId,
                      poNumber: po.poNumber || 'N/A',
                      procurementCode: po.fId,
                      invoiceNumber: po.invoiceNo || 'N/A',
                      contractorName: po.vendorName || 'Contractor',
                      amount: totalAmount,
                      subTotal: totalAmount,
                      invoiceClaim: parseFloat(po.invoiceClaim || 0),
                      retentionAmount: parseFloat(po.retainedAmount || 0),
                      date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                      invoiceData: po,
                      invoiceId: po.invoiceId
                    });
                  }
                }
              });
            }

            if (costType === "subcontractor") {
              setCostDetails(subcontractorCosts);
            } else if (costType === "all") {
              setCostDetails(prevDetails => [...prevDetails, ...subcontractorCosts]);
            }
          }

          // Labor costs
          if (costType === "labor" || costType === "all") {
            await _getGlobalSettings();
            const laborData =
              (isProjectInRedux && props.projectSummary?.laborData && Object.keys(props.projectSummary.laborData).length > 0)
                ? props.projectSummary.laborData
                : await props.getLaborCost({ id: projectId });

            // Get resource allocations filtered by stageId
            const usersList = await _getUsersList();
            let allocations = [];
            if (isProjectInRedux && Array.isArray(props.projectSummary?.resourceAllocations)) {
              allocations = props.projectSummary.resourceAllocations.filter(
                allocation => (
                  (allocation.stageId === finalStageId && !allocation.taskId) ||
                  (allocation.taskType === 'STAGE' && allocation.taskId === finalStageId)
                ) && allocation.status === 'APPROVED'
              );
            } else {
              const allocationsResponse = await props.getResourceAllocations({
                projectId,
                stageId: finalStageId, // Use finalStageId instead of taskId
                status: 'APPROVED',
                updateTaskAllocation: true // This filters for APPROVED status
              });

              // Handle response structure: getWorkAllocations returns { status: 'SUCCESS', data: [...] }
              allocations = allocationsResponse?.data || allocationsResponse || [];
            }

            setResourceAllocations(Array.isArray(allocations) ? allocations.map(allocation => ({
              ...allocation,
              userName: usersList.find(user => user.value === allocation.userId)?.key || 'Unknown User'
            })) : []);

            if (costType === "all") {
              const laborCosts = [{
                id: `labor-stage-${finalStageId}`,
                type: 'Timesheet',
                description: 'Resource time allocation',
                amount: laborData?.stageData?.[finalStageId] || 0,
                subTotal: laborData?.stageData?.[finalStageId] || 0,
                date: 'Various'
              }];

              setCostDetails(prevDetails => [...prevDetails, ...laborCosts]);
            }
          }
        } else {
          console.error('Stage not found:', finalStageId);
        }
        setIsLoading(false);
        return;
      }

      // Handle task-level breakdown (existing logic)
      // Find the task in the project
      let foundTask = null;
      let isVariation = false;

      // First check in regular tasks across all stages
      if (projectData.stages && Array.isArray(projectData.stages)) {
        foundTask = findTaskInStages(projectData.stages, taskId);
      }

      // If not found in regular tasks, check in variations/options
      if (!foundTask) {
        // Get options/variations: check Redux optionsList first, fallback to API
        const options = (isProjectInRedux && Array.isArray(props.projectSummary?.optionsList))
          ? props.projectSummary.optionsList
          : await props.getOptions(projectId);
        if (Array.isArray(options)) {
          foundTask = options.find(opt => opt.id === taskId);
          if (foundTask) isVariation = true;
        }
      }

      if (foundTask) {
        setTask(foundTask);

        // Get the stage hierarchy if this is a regular task (not a variation)
        if (!isVariation && foundTask.stageId) {
          const hierarchy = buildStageHierarchy(projectData.stages, foundTask.stageId);
          setStageHierarchy(hierarchy);
        }

        setCostTypeLabelFromType(costType);

        // Get cost details based on cost type
        if (costType === "material" || costType === "all") {
          // Material costs breakdown
          const materialCosts = [];
          const materialPoIds = new Set();

          // Collect PO IDs from materialPoCost (for filtering, but we'll fetch all POs once)
          if (Array.isArray(foundTask.materialPoCost)) {
            foundTask.materialPoCost.forEach(material => {
              // Process standard POs
              const poArray = idx(material, _ => _.budget['PO']);
              if (Array.isArray(poArray)) {
                poArray.forEach(poBudget => {
                  if (poBudget && poBudget.poId) {
                    materialPoIds.add(poBudget.poId);
                  }
                });
              }

              // Process warehouse POs
              const wpoArray = idx(material, _ => _.budget['WPO']);
              if (Array.isArray(wpoArray)) {
                wpoArray.forEach(poBudget => {
                  if (poBudget && poBudget.poId) {
                    materialPoIds.add(poBudget.poId);
                  }
                });
              }
            });
          }

          // Fetch all supplier POs in a single batch request and filter in memory
          const materialPODetails = await props.getvendorsPOList({ vendorType: 'Supplier', projectId, isCostBreakdown: true });
          const materialList = materialPODetails?.data?.list || materialPODetails?.list || [];

          if (Array.isArray(materialList) && materialList.length > 0) {
            materialList.forEach(po => {
              // Only process POs that are in our materialPoIds set (if we have any)
              // If materialPoIds is empty, process all (shouldn't happen, but safe fallback)
              if (materialPoIds.size === 0 || materialPoIds.has(po.poId)) {
                // Filter invoice items by taskId to match what's stored in materialPoCost
                // A PO can have invoices for multiple tasks, we only want this task's invoices
                if (po.items) {
                  const parsedItems = typeof po.items === 'string' ? JSON.parse(po.items) : po.items;
                  let taskTotalAmount = 0;

                  if (Array.isArray(parsedItems)) {
                    parsedItems.forEach(entry => {
                      if (!entry) return;
                      // Check if entry is attributed to this task
                      if (entry.taskId === taskId || (entry.taskType === 'TASK' && entry.taskId === taskId)) {
                        // Supplier PO invoice: entry.items is an array of item lines
                        const nestedItems = idx(entry, _ => _.items);
                        if (Array.isArray(nestedItems)) {
                          nestedItems.forEach(item => {
                            const amountPaid = parseFloat(item?.amountPaid || 0) || 0;
                            if (amountPaid !== 0) {
                              taskTotalAmount += amountPaid;
                            }
                          });
                        }
                      }
                    });
                  }

                  // Only add this invoice if it has items attributed to this task
                  if (taskTotalAmount !== 0) {
                    materialCosts.push({
                      id: po.invoiceId || `po-${Math.random()}`,
                      type: 'PO',
                      poId: po.poId,
                      poNumber: po.poNumber || 'N/A',
                      procurementCode: po.fId,
                      materialName: po.vendorName || 'Unknown Material',
                      partNo: po.poId || 'N/A',
                      invoiceNo: po.invoiceNo || 'N/A',
                      amount: taskTotalAmount,
                      subTotal: taskTotalAmount,
                      date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                      invoiceData: po,
                      invoiceId: po.invoiceId
                    });
                  }
                } else {
                  // Fallback: if no items structure, use the PO-level amountPaid
                  // This maintains backward compatibility but may not be accurate for multi-task POs
                  // Only include if this PO is in our set (to avoid including unrelated POs)
                  if (materialPoIds.has(po.poId)) {
                    materialCosts.push({
                      id: po.invoiceId || `po-${Math.random()}`,
                      type: 'PO',
                      poId: po.poId,
                      poNumber: po.poNumber || 'N/A',
                      procurementCode: po.fId,
                      materialName: po.vendorName || 'Unknown Material',
                      partNo: po.poId || 'N/A',
                      invoiceNo: po.invoiceNo || 'N/A',
                      amount: parseFloat(po.amountPaid || 0),
                      subTotal: parseFloat(po.subTotal || 0),
                      date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                      invoiceData: po,
                      invoiceId: po.invoiceId
                    });
                  }
                }
              }
            });
          }

          if (costType === "material") {
            setCostDetails(materialCosts);
          } else {
            // For "all" type we'll initially set to material costs and add others later
            setCostDetails(materialCosts);
          }
        }

        if (costType === "labor" || costType === "all") {
          await _getGlobalSettings();
          // Labor costs breakdown: read from Redux if present, else fetch
          const laborData =
            (isProjectInRedux && props.projectSummary?.laborData && Object.keys(props.projectSummary.laborData).length > 0)
              ? props.projectSummary.laborData
              : await props.getLaborCost({ id: projectId });

          // Get resource allocations
          const usersList = await _getUsersList();
          let allocations = [];
          if (isProjectInRedux && Array.isArray(props.projectSummary?.resourceAllocations)) {
            allocations = props.projectSummary.resourceAllocations.filter(
              allocation => allocation.taskId === taskId && allocation.status === 'APPROVED'
            );
          } else {
            const allocationsResponse = await props.getResourceAllocations({
              projectId,
              taskId,
              status: 'APPROVED',
              updateTaskAllocation: true // This filters for APPROVED status
            });

            // Handle response structure: getWorkAllocations returns { status: 'SUCCESS', data: [...] }
            allocations = allocationsResponse?.data || allocationsResponse || [];
          }

          setResourceAllocations(Array.isArray(allocations) ? allocations.map(allocation => ({
            ...allocation,
            userName: usersList.find(user => user.value === allocation.userId)?.key || 'Unknown User'
          })) : []);

          if (costType === "all") {
            const laborCosts = [{
              id: `labor-${taskId}`,
              type: 'Timesheet',
              description: 'Resource time allocation',
              amount: laborData?.taskData?.[taskId] || 0,
              subTotal: laborData?.taskData?.[taskId] || 0,
              date: 'Various'
            }];

            setCostDetails(prevDetails => [...prevDetails, ...laborCosts]);
          }
        }

        if (costType === "subcontractor" || costType === "all") {
          // Subcontractor costs breakdown
          const subcontractorCosts = [];
          const subcontractorPoIds = new Set();

          // Collect PO IDs from subContractorInvoiceCost (for filtering)
          if (Array.isArray(foundTask.subContractorInvoiceCost)) {
            foundTask.subContractorInvoiceCost.forEach(invoice => {
              if (invoice && invoice.poId) {
                subcontractorPoIds.add(invoice.poId);
              }
            });
          }

          // Fetch all subcontractor POs in a single batch request and filter in memory
          const subcontractorPODetails = await props.getvendorsPOList({ vendorType: 'sub-contractor', projectId, isCostBreakdown: true });
          const subcontractorList = subcontractorPODetails?.data?.list || subcontractorPODetails?.list || [];

          if (Array.isArray(subcontractorList) && subcontractorList.length > 0) {
            subcontractorList.forEach(po => {
              // Only process POs that are in our subcontractorPoIds set (if we have any)
              // If subcontractorPoIds is empty, process all (shouldn't happen, but safe fallback)
              if (subcontractorPoIds.size === 0 || subcontractorPoIds.has(po.poId)) {
                // Filter invoice items by taskId to match what's stored in subContractorInvoiceCost
                // A PO can have invoices for multiple tasks, we only want this task's invoices
                let taskTotalAmount = 0;
                let taskInvoiceClaim = 0;
                let taskRetentionAmount = 0;

                if (po.items) {
                  const parsedItems = typeof po.items === 'string' ? JSON.parse(po.items) : po.items;

                  if (Array.isArray(parsedItems)) {
                    parsedItems.forEach(entry => {
                      if (!entry) return;
                      // Check if entry is attributed to this task
                      if (entry.taskId === taskId || (entry.taskType === 'TASK' && entry.taskId === taskId)) {
                        // Subcontractor WO invoice: entry.claimAmount or entry.invoiceClaim is the GST-exclusive amount, falling back to amountPaid
                        const claim = entry?.claimAmount !== undefined && entry?.claimAmount !== null
                          ? entry.claimAmount
                          : (entry?.invoiceClaim !== undefined && entry?.invoiceClaim !== null ? entry.invoiceClaim : (entry?.amountPaid || 0));
                        const amountPaid = parseFloat(claim) || 0;
                        if (amountPaid !== 0) {
                          taskTotalAmount += amountPaid;
                        }
                        // Also capture invoice claim and retention if available at entry level
                        taskInvoiceClaim += parseFloat(entry?.claimAmount || entry?.invoiceClaim || 0) || 0;
                        taskRetentionAmount += parseFloat(entry?.retentionAmount || 0) || 0;
                      }
                    });
                  }
                }

                // Only add this invoice if it has items attributed to this task
                // If no items structure or no task-specific items found, fall back to PO-level values
                // (but only if this PO is in our set to avoid including unrelated POs)
                if (taskTotalAmount !== 0) {
                  subcontractorCosts.push({
                    id: po.invoiceId || `sc-${Math.random()}`,
                    type: 'Subcontractor Invoice',
                    poId: po.poId,
                    poNumber: po.poNumber || 'N/A',
                    procurementCode: po.fId,
                    invoiceNumber: po.invoiceNo || 'N/A',
                    contractorName: po.vendorName || 'Contractor',
                    amount: taskTotalAmount,
                    subTotal: taskTotalAmount,
                    invoiceClaim: taskInvoiceClaim > 0 ? taskInvoiceClaim : parseFloat(po.invoiceClaim || 0),
                    retentionAmount: taskRetentionAmount > 0 ? taskRetentionAmount : parseFloat(po.retainedAmount || 0),
                    date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                    invoiceData: po,
                    invoiceId: po.invoiceId
                  });
                } else if (subcontractorPoIds.has(po.poId)) {
                  // Fallback: if no items structure or no task-specific items, use PO-level values
                  // Only include if this PO is in our set (to avoid including unrelated POs)
                  const finalSubTotal = toDecimal(po.subTotal || 0);

                  subcontractorCosts.push({
                    id: po.invoiceId || `sc-${Math.random()}`,
                    type: 'Subcontractor Invoice',
                    poId: po.poId,
                    poNumber: po.poNumber || 'N/A',
                    procurementCode: po.fId,
                    invoiceNumber: po.invoiceNo || 'N/A',
                    contractorName: po.vendorName || 'Contractor',
                    amount: finalSubTotal.gt(0) ? finalSubTotal.toNumber() : 0,
                    subTotal: finalSubTotal.gt(0) ? finalSubTotal.toNumber() : 0,
                    invoiceClaim: parseFloat(po.invoiceClaim || 0),
                    retentionAmount: parseFloat(po.retainedAmount || 0),
                    date: new Date(po.invoiceDate).toLocaleDateString() || 'N/A',
                    invoiceData: po,
                    invoiceId: po.invoiceId
                  });
                }
              }
            });
          }

          if (costType === "subcontractor") {
            setCostDetails(subcontractorCosts);
          } else if (costType === "all") {
            setCostDetails(prevDetails => [...prevDetails, ...subcontractorCosts]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching cost breakdown data:", error);
      setCostDetails([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    projectId,
    costType,
    taskId,
    finalStageId,
    isStageBreakdown,
    props.getProjectSummary,
    props.getvendorsPOList,
    props.getLaborCost,
    props.getResourceAllocations,
    props.getOptions,
    props.getUsersList,
    props.getGlobalSettings,
    props.projectSummary
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    project,
    task,
    stage,
    costDetails,
    isLoading,
    costTypeLabel,
    resourceAllocations,
    stageHierarchy,
    globalSettings
  };
};
